import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import {
  ApiService,
  AppointmentModel,
  getAppointmentRequestStatusColor,
  getAppointmentRequestStatusText,
  getWaitListStatusColor,
  getWaitListStatusText,
  MessageTemplate,
  Patient,
  Permission,
  ProgressIndicatorService,
  SendPatientMessageMethod,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { filter, map, startWith, Subject, switchMap } from 'rxjs'

import {
  AppointmentWaitlistDialogComponent,
  AppointmentWaitlistDialogResult,
} from '../../../appointment-waitlist-dialog'

interface NotifyPatientEvent {
  appointment: AppointmentModel
  method: SendPatientMessageMethod
}

@Component({
  selector: 'mmx-wait-list-status-icon',
  templateUrl: './wait-list-status-icon.component.html',
  standalone: false,
})
export class WaitListStatusIconComponent implements OnChanges {
  @Input() public appointment: AppointmentModel
  @Input() public patient: Patient
  @Output() public appointmentUpdated = new EventEmitter<AppointmentModel>()

  public readonly Permission = Permission
  public process = this.progressService.async()
  private changes$ = new Subject<void>()

  public statusText$ = this.changes$.pipe(
    startWith(null),
    map(() => {
      if (this.appointment.request) {
        return getAppointmentRequestStatusText(this.appointment.request.status)
      } else {
        return getWaitListStatusText(this.appointment.waitList?.status)
      }
    }),
  )

  public statusColor$ = this.changes$.pipe(
    startWith(null),
    map(() => {
      if (this.appointment.request) {
        return getAppointmentRequestStatusColor(this.appointment.request.status)
      } else {
        return getWaitListStatusColor(this.appointment.waitList?.status)
      }
    }),
  )

  public requestLink$ = this.changes$.pipe(
    startWith(null),
    map(() =>
      this.appointment.request
        ? [
          '/patient',
          this.appointment.patientId,
          'appointment-requests',
          this.appointment.request.id,
        ]
        : undefined,
    ),
  )

  constructor(
    private apiService: ApiService,
    private dialog: MatDialog,
    private progressService: ProgressIndicatorService,
    private translate: TranslateService,
  ) {}

  public ngOnChanges(): void {
    this.changes$.next()
  }

  public onOpenWaitlistDialog() {
    // fetch patient data again to get updated information
    this.apiService
      .getPatient(this.patient.id, true)
      .pipe(
        switchMap((patient) => {
          return this.dialog
            .open(AppointmentWaitlistDialogComponent, {
              panelClass: 'dialog-form',
              width: '800px',
              data: {
                appointment: this.appointment,
                patient,
              },
            })
            .afterClosed()
        }),
        filter(Boolean),
      )
      .subscribe(({ appointment }: AppointmentWaitlistDialogResult) =>
        this.appointmentUpdated.emit(appointment),
      )
  }

  public onNotifyPatient({ appointment, method }: NotifyPatientEvent) {
    this.process.start()
    this.apiService
      .sendPatientMessage(appointment.patientId, {
        type: MessageTemplate.TYPE.WAIT_LIST_REACH_OUT,
        method,
        appointmentId: appointment.id,
      })
      .subscribe({
        next: () => {
          this.process.stop(
            this.translate.instant('wait-list.menu.reach-out.sent'),
          )
        },
        error: () =>
          this.process.stop(
            this.translate.instant('wait-list.menu.reach-out.error'),
          ),
      })
  }

  public removeWaitList() {
    this.process.start()
    this.apiService
      .patchAppointment(this.appointment.id, { waitList: false })
      .subscribe({
        next: (appointment) => {
          this.appointmentUpdated.emit(appointment)
          this.process.stop(
            this.translate.instant('dialog.waitlist.status.removed'),
          )
        },
        error: () =>
          this.process.stop(
            this.translate.instant('dialog.waitlist.status.error'),
          ),
      })
  }
}
